(() => {
  const STORAGE_KEY = 'jr_postcode_au';
  const STANDARD_CBM = 1.0;
  const RATE_TIMEOUT_MS = 20_000;

  function normalisePostcode(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '')
      .slice(0, 4);
  }

  function postcodeToAuState(postcode) {
    if (!/^[0-9]{4}$/.test(postcode)) return '';
    const n = parseInt(postcode, 10);

    if (n >= 200 && n <= 299) return 'Australian Capital Territory';
    if (n >= 2600 && n <= 2618) return 'Australian Capital Territory';
    if (n >= 2900 && n <= 2920) return 'Australian Capital Territory';

    if (n >= 1000 && n <= 2599) return 'New South Wales';
    if (n >= 2620 && n <= 2899) return 'New South Wales';
    if (n >= 2921 && n <= 2999) return 'New South Wales';

    if (n >= 3000 && n <= 3999) return 'Victoria';
    if (n >= 8000 && n <= 8999) return 'Victoria';

    if (n >= 4000 && n <= 4999) return 'Queensland';
    if (n >= 9000 && n <= 9999) return 'Queensland';

    if (n >= 5000 && n <= 5799) return 'South Australia';

    if (n >= 6000 && n <= 6797) return 'Western Australia';

    if (n >= 7000 && n <= 7799) return 'Tasmania';

    if (n >= 800 && n <= 899) return 'Northern Territory';
    if (n >= 900 && n <= 999) return 'Northern Territory';

    return '';
  }

  function isValidAuPostcode(value) {
    return /^[0-9]{4}$/.test(value);
  }

  function moneyFromCents(cents) {
    try {
      return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function escapeHtml(text) {
    return String(text || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function isLocalDeliveryRate(rateName) {
    return /local\s+delivery/i.test(String(rateName || ''));
  }

  function hasCbmProperty(properties) {
    if (!properties || typeof properties !== 'object') return false;
    const keys = Object.keys(properties).map((k) => String(k || '').toLowerCase());
    const supportedKeys = ['total_cbm', 'cbm_total', 'cbm', 'volume_cbm', 'cubic_meters'];
    const matchingKey = supportedKeys.find((k) => keys.includes(k));
    if (!matchingKey) return false;
    const originalKey = Object.keys(properties).find((k) => String(k || '').toLowerCase() === matchingKey);
    const value = originalKey ? properties[originalKey] : '';
    return String(value || '').trim() !== '';
  }

  function normalizeVariantId(value) {
    const id = String(value || '').trim();
    if (!/^[1-9][0-9]*$/.test(id)) return '';
    return id;
  }

  async function fetchCart() {
    const res = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
    return res.json();
  }

  async function addTempQuoteItem(variantId, cbmValue, signal) {
    const id = normalizeVariantId(variantId);
    if (!id) throw new Error('Missing shipping quote variant id.');

    const payload = {
      id: Number(id),
      quantity: 1,
      properties: {
        cbm: String(Number.isFinite(cbmValue) ? cbmValue.toFixed(4) : STANDARD_CBM.toFixed(4)),
        _jr_shipping_quote: '1',
      },
    };

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

    const text = await res.text().catch(() => '');
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      const err = new Error(data?.description || data?.message || `Could not add shipping quote item (${res.status}).`);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return data;
  }

  async function removeLineItemByKey(lineKey, signal) {
    const key = String(lineKey || '').trim();
    if (!key) return;

    const payload = { id: key, quantity: 0 };
    await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    }).catch(() => null);
  }

  async function fetchShippingRates(postcode, province, signal) {
    const url = new URL('/cart/shipping_rates.json', window.location.origin);
    url.searchParams.set('shipping_address[zip]', postcode);
    url.searchParams.set('shipping_address[country]', 'AU');
    if (province) url.searchParams.set('shipping_address[province]', province);
    // Some storefronts prefer province_code (e.g. NSW) over full province name; add both when possible.
    const provinceCodeMap = {
      'Australian Capital Territory': 'ACT',
      'New South Wales': 'NSW',
      'Northern Territory': 'NT',
      Queensland: 'QLD',
      'South Australia': 'SA',
      Tasmania: 'TAS',
      Victoria: 'VIC',
      'Western Australia': 'WA',
    };
    if (province && provinceCodeMap[province]) url.searchParams.set('shipping_address[province_code]', provinceCodeMap[province]);

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });

    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    if (!res.ok) {
      if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) return data.shipping_rates;
      const message =
        data?.error ||
        data?.message ||
        data?.errors?.shipping_address ||
        `Could not get delivery rates (${res.status}).`;
      const err = new Error(message);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
  }

  async function prepareShippingRates(postcode, province, signal) {
    const url = new URL('/cart/prepare_shipping_rates.json', window.location.origin);

    const provinceCodeMap = {
      'Australian Capital Territory': 'ACT',
      'New South Wales': 'NSW',
      'Northern Territory': 'NT',
      Queensland: 'QLD',
      'South Australia': 'SA',
      Tasmania: 'TAS',
      Victoria: 'VIC',
      'Western Australia': 'WA',
    };

    const params = new URLSearchParams();
    params.set('shipping_address[zip]', postcode);
    params.set('shipping_address[country]', 'AU');
    if (province) {
      params.set('shipping_address[province]', province);
      if (provinceCodeMap[province]) params.set('shipping_address[province_code]', provinceCodeMap[province]);
    }

    const res = await fetch(url.toString(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: params.toString(),
      signal,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Prepare shipping rates failed (${res.status}).`);
      err.status = res.status;
      err.data = text;
      throw err;
    }
  }

  async function fetchAsyncShippingRates(postcode, province, signal) {
    const url = new URL('/cart/async_shipping_rates.json', window.location.origin);
    // Keep address params attached for compatibility with storefront implementations.
    url.searchParams.set('shipping_address[zip]', postcode);
    url.searchParams.set('shipping_address[country]', 'AU');
    if (province) url.searchParams.set('shipping_address[province]', province);
    const provinceCodeMap = {
      'Australian Capital Territory': 'ACT',
      'New South Wales': 'NSW',
      'Northern Territory': 'NT',
      Queensland: 'QLD',
      'South Australia': 'SA',
      Tasmania: 'TAS',
      Victoria: 'VIC',
      'Western Australia': 'WA',
    };
    if (province && provinceCodeMap[province]) url.searchParams.set('shipping_address[province_code]', provinceCodeMap[province]);

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });

    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    if (res.status === 202) {
      const err = new Error('Shipping rates are still being calculated.');
      err.status = 202;
      err.data = data;
      throw err;
    }

    if (!res.ok) {
      if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length) return data.shipping_rates;
      const message =
        data?.error ||
        data?.message ||
        data?.errors?.shipping_address ||
        `Could not get delivery rates (${res.status}).`;
      const err = new Error(message);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return Array.isArray(data?.shipping_rates) ? data.shipping_rates : [];
  }

  function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const timer = window.setTimeout(() => resolve(), ms);
      if (!signal) return;

      const onAbort = () => {
        window.clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      signal.addEventListener('abort', onAbort);
    });
  }

  class JrDeliveryEstimator {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('.jr-delivery__input');
      this.button = root.querySelector('.jr-delivery__btn');
      this.spinner = this.button?.querySelector?.('.loading__spinner') || null;
      this.status = root.querySelector('.jr-delivery__status');
      this.rates = root.querySelector('.jr-delivery__rates');
      this.standardCbm = (() => {
        const raw = root?.dataset?.jrStandardCbm || '';
        const v = Number(raw);
        return Number.isFinite(v) && v > 0 ? v : STANDARD_CBM;
      })();
      this.quoteVariantId = normalizeVariantId(root?.dataset?.jrShippingQuoteVariantId);
      this.fallbackVariantId = normalizeVariantId(root?.dataset?.jrFallbackVariantId);
      this.abortController = null;
      this.hasRunOnce = false;
      this.lastRequestedPostcode = '';
      this.retryTimer = 0;
      this.retryCount = 0;

      if (!this.input || !this.button || !this.status || !this.rates) return;

      const saved = normalisePostcode(localStorage.getItem(STORAGE_KEY));
      if (saved) this.input.value = saved;

      this.button.addEventListener('click', () => this.run({ source: 'manual' }));
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.run({ source: 'manual' });
      });

      document.addEventListener('jr:postcode-change', (e) => {
        const detail = e?.detail || {};
        if (detail.source === 'delivery-estimator') return;
        const postcode = normalisePostcode(detail.postcode);
        if (!postcode) return;
        this.input.value = postcode;

        // If the customer already used the estimator once, auto-refresh rates on postcode changes.
        if (!this.hasRunOnce) return;
        if (postcode === this.lastRequestedPostcode) return;
        window.setTimeout(() => {
          if (this.input) this.run({ source: 'postcode-change' });
        }, 120);
      });
    }

    setStatus(message) {
      const text = String(message || '').trim();
      this.status.textContent = text;
      this.status.hidden = !text;
    }

    clearRates() {
      this.rates.hidden = true;
      this.rates.innerHTML = '';
    }

    setRates(rates) {
      if (!Array.isArray(rates) || rates.length === 0) {
        this.clearRates();
        return;
      }

      const localRates = rates.filter((r) => isLocalDeliveryRate(r?.name));
      const displayRates = localRates.length ? localRates : rates;

      this.rates.hidden = false;
      this.rates.innerHTML = `
        <ul class="jr-delivery__rate-list" role="list">
          ${displayRates
            .map((r) => {
              const price = typeof r.price === 'string' ? Number(r.price) : r.price;
              const cents = Number.isFinite(price) ? Math.round(price * 100) : 0;

              const rawName = String(r?.name || 'Delivery').trim();
              let name = rawName;
              let eta = '';

              const digitIndex = rawName.search(/\d/);
              if (digitIndex > 0) {
                name = rawName.slice(0, digitIndex).trim().replace(/[-–]$/, '').trim();
                eta = rawName.slice(digitIndex).trim();
              }

              if (!name) name = rawName || 'Delivery';

              if (localRates.length) {
                eta = rawName;
                name = 'Local delivery';
              }

              const priceLabel = cents === 0 ? 'Free' : moneyFromCents(cents);
              return `
                <li class="jr-delivery__rate">
                  <div class="jr-delivery__rate-main">
                    <div class="jr-delivery__rate-name">${escapeHtml(name)}</div>
                    ${eta ? `<div class="jr-delivery__rate-eta">${escapeHtml(eta)}</div>` : ''}
                  </div>
                  <div class="jr-delivery__rate-price" data-jr-delivery-price="${cents === 0 ? 'free' : 'paid'}">${escapeHtml(
                priceLabel
              )}</div>
                </li>
              `;
            })
            .join('')}
        </ul>
      `;
    }

    async run({ source } = {}) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      let timedOut = false;
      let timeoutId = 0;

      if (this.retryTimer) window.clearTimeout(this.retryTimer);
      this.retryTimer = 0;
      this.retryCount = 0;

      const postcode = normalisePostcode(this.input.value);
      this.input.value = postcode;
      const debug = (() => {
        try {
          return new URL(window.location.href).searchParams.get('jr_debug') === '1';
        } catch {
          return false;
        }
      })();

      if (!isValidAuPostcode(postcode)) {
        this.setRates([]);
        this.setStatus('Enter a valid 4-digit postcode.');
        return;
      }

      timeoutId = window.setTimeout(() => {
        timedOut = true;
        try {
          this.abortController?.abort();
        } catch {
          // ignore
        }
      }, RATE_TIMEOUT_MS);

      this.hasRunOnce = true;
      this.lastRequestedPostcode = postcode;

      localStorage.setItem(STORAGE_KEY, postcode);
      const state = postcodeToAuState(postcode) || '';

      document.dispatchEvent(
        new CustomEvent('jr:postcode-change', {
          detail: { postcode, state, source: 'delivery-estimator' },
        })
      );

      this.clearRates();
      this.setStatus('Checking delivery options');
      this.button.disabled = true;
      this.root.classList.add('jr-delivery--loading');
      this.root.setAttribute('aria-busy', 'true');
      this.button.classList.add('loading');
      this.spinner?.classList?.remove?.('hidden');

      let missingCbmCount = 0;
      let prepareFailed = false;
      let shippableCount = 0;
      let nonShippableCount = 0;
      let tempLineKey = '';

      try {
        let cart = await fetchCart();

        if (!cart?.items?.length) {
          const quoteVariantId = this.quoteVariantId || this.fallbackVariantId;
          if (!quoteVariantId) {
            this.setStatus('Add to cart for an exact delivery quote.');
            return;
          }

          const added = await addTempQuoteItem(quoteVariantId, this.standardCbm, signal);
          tempLineKey = String(added?.key || '').trim();
          cart = await fetchCart();
        }

        shippableCount = cart.items.filter((it) => it && it.requires_shipping).length;
        nonShippableCount = cart.items.length - shippableCount;

        if (!shippableCount) {
          this.setStatus('No shippable items in your cart. Delivery options will be shown at checkout when needed.');
          return;
        }

        missingCbmCount = cart.items.filter((it) => it && it.requires_shipping && !hasCbmProperty(it.properties)).length;

        let rates = [];
        let lastErr = null;

        // Prepare (kicks off Shopify's async calculation).
        try {
          await prepareShippingRates(postcode, state, signal);
        } catch {
          prepareFailed = true;
        }

        // Poll async endpoint first, fall back to shipping_rates.json for compatibility.
        for (let attempt = 0; attempt < 14; attempt += 1) {
          try {
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            rates = await fetchAsyncShippingRates(postcode, state, signal);
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            const status = e?.status;
            const msg = String(e?.message || '').toLowerCase();
            const retryable = status === 422 || status === 202 || msg.includes('not ready') || msg.includes('calculat');
            if (!retryable) break;
            await sleep(650 + attempt * 450, signal);
          }
        }

        if (lastErr && (!rates || rates.length === 0)) {
          // Fallback polling against shipping_rates.json (older behaviour).
          for (let attempt = 0; attempt < 8; attempt += 1) {
            try {
              if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
              rates = await fetchShippingRates(postcode, state, signal);
              lastErr = null;
              break;
            } catch (e) {
              lastErr = e;
              const status = e?.status;
              const msg = String(e?.message || '').toLowerCase();
              const retryable = status === 422 || status === 202 || msg.includes('not ready') || msg.includes('calculat');
              if (!retryable) break;
              await sleep(650 + attempt * 450, signal);
            }
          }
        }

        if (lastErr && (!rates || rates.length === 0)) throw lastErr;
        if (!rates.length) {
          this.setStatus('No delivery options were returned for this postcode. Delivery options will be shown at checkout.');
          return;
        }

        this.setRates(rates);
        const localOnly = rates.some((r) => isLocalDeliveryRate(r?.name));
        const tail = nonShippableCount ? ` Shipping applies to ${shippableCount} item(s) in your cart.` : '';
        const prefix = localOnly ? `Local delivery for ${postcode}:` : `Estimated delivery options for ${postcode}:`;
        const standardNote = tempLineKey ? ` Standard estimate based on ${this.standardCbm.toFixed(1)} CBM.` : '';
        this.setStatus(`${prefix}${standardNote}${tail} Final delivery options shown at checkout.`);
      } catch (err) {
        if (err?.name === 'AbortError') {
          if (timedOut) {
            this.clearRates();
            this.setStatus('Delivery estimate is taking longer than expected. Please proceed to checkout to see an estimated rate.');
          }
          return;
        }
        this.clearRates();
        if (err?.status === 422) {
          const parts = ['Calculating delivery rates... this can take a moment.'];
          if (missingCbmCount) {
            parts.push('Some items are missing CBM (volume), which may prevent accurate delivery quotes.');
          }
          if (prepareFailed && debug) {
            parts.push('Debug: prepare_shipping_rates failed.');
          }
          this.setStatus(parts.join(' '));

          // Auto-retry a couple of times so customers don't need to click twice.
          if (!signal.aborted && this.retryCount < 2) {
            this.retryCount += 1;
            this.retryTimer = window.setTimeout(() => {
              if (signal.aborted) return;
              this.run({ source: source || 'auto-retry' });
            }, 1400);
          }
          return;
        }
        this.setStatus(
          debug && err?.status ? `${err?.message || 'Delivery estimate failed.'} (HTTP ${err.status})` : err?.message || 'Delivery estimate failed.'
        );
      } finally {
        if (tempLineKey) {
          await removeLineItemByKey(tempLineKey);
        }
        if (timeoutId) window.clearTimeout(timeoutId);
        this.button.classList.remove('loading');
        this.spinner?.classList?.add?.('hidden');
        this.root.classList.remove('jr-delivery--loading');
        this.root.removeAttribute('aria-busy');
        this.button.disabled = false;
      }
    }
  }

  function initWithin(container) {
    const scope = container && container.querySelectorAll ? container : document;
    scope.querySelectorAll('[data-jr-delivery-estimator]').forEach((el) => {
      if (el.dataset.jrDeliveryInit === '1') return;
      try {
        if (el.closest && el.closest('quick-add-modal')) {
          el.dataset.jrDeliverySurface = 'quickadd';
        }
      } catch {
        // ignore
      }
      el.dataset.jrDeliveryInit = '1';
      new JrDeliveryEstimator(el);
    });
  }

  function observeDynamicMounts() {
    const root = document.body;
    if (!root || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.('[data-jr-delivery-estimator]')) {
            initWithin(node.parentElement || node);
            continue;
          }
          if (node.querySelector?.('[data-jr-delivery-estimator]')) {
            initWithin(node);
          }
        }
      }
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    initWithin(document);
    observeDynamicMounts();

    document.addEventListener('jr:quickadd-loaded', (e) => {
      const root = e?.detail?.root;
      if (root instanceof Element) initWithin(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
